import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client/react';
import { useParams, useNavigate } from 'react-router';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  GetInvoiceDocument,
  GetInvoiceQuery,
  GetInvoiceQueryVariables,
  UpdateInvoiceDocument,
  UpdateInvoiceMutation,
  UpdateInvoiceMutationVariables,
  IssueInvoiceDocument,
  IssueInvoiceMutation,
  IssueInvoiceMutationVariables,
  DeleteInvoiceDocument,
  DeleteInvoiceMutation,
  DeleteInvoiceMutationVariables,
  UndeleteInvoiceDocument,
  UndeleteInvoiceMutation,
  UndeleteInvoiceMutationVariables,
  MarkInvoicePaidDocument,
  MarkInvoicePaidMutation,
  MarkInvoicePaidMutationVariables,
  MarkInvoiceUnpaidDocument,
  MarkInvoiceUnpaidMutation,
  MarkInvoiceUnpaidMutationVariables,
  CopyInvoiceDocument,
  CopyInvoiceMutation,
  CopyInvoiceMutationVariables,
  SendPaymentConfirmationDocument,
  SendPaymentConfirmationMutation,
  SendPaymentConfirmationMutationVariables,
  SendInvoiceInformationEmailDocument,
  SendInvoiceInformationEmailMutation,
  SendInvoiceInformationEmailMutationVariables,
  AddInvoiceCommentDocument,
  AddInvoiceCommentMutation,
  AddInvoiceCommentMutationVariables,
  UpdateInvoiceCommentDocument,
  UpdateInvoiceCommentMutation,
  UpdateInvoiceCommentMutationVariables,
  RemoveInvoiceCommentDocument,
  RemoveInvoiceCommentMutation,
  RemoveInvoiceCommentMutationVariables,
  AddInvoiceRowDocument,
  AddInvoiceRowMutation,
  AddInvoiceRowMutationVariables,
  UpdateInvoiceRowDocument,
  UpdateInvoiceRowMutation,
  UpdateInvoiceRowMutationVariables,
  RemoveInvoiceRowDocument,
  RemoveInvoiceRowMutation,
  RemoveInvoiceRowMutationVariables,
  MoveInvoiceRowUpDocument,
  MoveInvoiceRowUpMutation,
  MoveInvoiceRowUpMutationVariables,
  MoveInvoiceRowDownDocument,
  MoveInvoiceRowDownMutation,
  MoveInvoiceRowDownMutationVariables,
  DeleteInvoicePaymentDocument,
  DeleteInvoicePaymentMutation,
  DeleteInvoicePaymentMutationVariables,
  SetInvoiceUnitAssignmentsDocument,
  SetInvoiceUnitAssignmentsMutation,
  SetInvoiceUnitAssignmentsMutationVariables,
} from '../../generated/graphql';
import { FormInput } from '../../components/form-input';
import { useAlert } from '@dwelvio/react-alert';
import { useApp } from '../../components/app-context';
import { CommentList } from '../../components/comments-list';
import { InputComment } from '../../components/input-comment';
import { BasePage } from '../../components/base-page';
import { appPaths } from '../../app-paths';
import { InvoiceRowEditor } from './components/invoice-row-editor';
import { InvoicePaymentList } from '../../components/invoice-payment-list';
import { ConfirmationDialog } from '../../components/confirmation-dialog';
import { MoneyTransactionDialog } from '../money-transaction-list/components/money-transaction-dialog';
import { DeletedItemBanner } from '../../components/deleted-item-banner';
import { InvoiceIncomeAssignmentPanel, UnitAssignmentRow } from './components/invoice-income-assignment';
import { AddIcon } from '../../components/icons';

interface FormFields {
  invoiceNo: string;
  refNo: string;
  description: string;
  issuedAt: string;
  dueDate: string;
}

export function InvoiceDetailPage() {
  const app = useApp();
  const { show } = useAlert();
  const navigate = useNavigate();
  const { invoiceId } = useParams();

  const [formData, setFormData] = useState<FormFields>({
    invoiceNo: '',
    refNo: '',
    description: '',
    issuedAt: '',
    dueDate: '',
  });

  const [showMarkPaidDialog, setShowMarkPaidDialog] = useState(false);
  const [paidDate, setPaidDate] = useState(new Date().toISOString().split('T')[0]);
  const [paymentToDelete, setPaymentToDelete] = useState<string | null>(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [showIssueDialog, setShowIssueDialog] = useState(false);
  const [showSendInvoiceInfoDialog, setShowSendInvoiceInfoDialog] = useState(false);
  const [invoiceInfoEmails, setInvoiceInfoEmails] = useState('');
  const [includeDefaultRecipients, setIncludeDefaultRecipients] = useState(true);
  const [invoiceInfoEmailsError, setInvoiceInfoEmailsError] = useState('');

  const [fetchInvoice, { data, loading: initialLoading, error, refetch }] = useLazyQuery<
    GetInvoiceQuery,
    GetInvoiceQueryVariables
  >(GetInvoiceDocument);

  useEffect(() => {
    if (invoiceId) {
      fetchInvoice({ variables: { id: invoiceId } });
    }
  }, [invoiceId, fetchInvoice]);

  const invoice = data?.getInvoice;

  useEffect(() => {
    if (invoice) {
      setFormData({
        invoiceNo: invoice.invoiceNo || '',
        refNo: invoice.refNo || '',
        description: invoice.description || '',
        issuedAt: invoice.issuedAt ? new Date(invoice.issuedAt).toISOString().substring(0, 10) : '',
        dueDate: invoice.dueDate ? new Date(invoice.dueDate).toISOString().substring(0, 10) : '',
      });
    }
  }, [invoice]);

  const [updateInvoice, { loading: updateLoading }] = useMutation<
    UpdateInvoiceMutation,
    UpdateInvoiceMutationVariables
  >(UpdateInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa upraviť faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Faktúra bola úspešne upravená.');
      refetch?.();
    },
  });

  const [issueInvoice, { loading: issueLoading }] = useMutation<IssueInvoiceMutation, IssueInvoiceMutationVariables>(
    IssueInvoiceDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa vystaviť faktúru.', e.message);
      },
      onCompleted: () => {
        show.info('Faktúra bola úspešne vystavená.');
        setShowIssueDialog(false);
        refetch?.();
      },
    },
  );

  const [deleteInvoice, { loading: deleteLoading }] = useMutation<
    DeleteInvoiceMutation,
    DeleteInvoiceMutationVariables
  >(DeleteInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa zmazať faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Faktúra bola úspešne zmazaná.');
      refetch?.();
    },
  });

  const [undeleteInvoice, { loading: undeleteLoading }] = useMutation<
    UndeleteInvoiceMutation,
    UndeleteInvoiceMutationVariables
  >(UndeleteInvoiceDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa obnoviť faktúru.', e.message);
    },
    onCompleted: () => {
      show.info('Faktúra bola úspešne obnovená.');
      refetch?.();
    },
  });

  const [markPaid, { loading: markPaidLoading }] = useMutation<
    MarkInvoicePaidMutation,
    MarkInvoicePaidMutationVariables
  >(MarkInvoicePaidDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa označiť faktúru ako uhradenú.', e.message);
    },
    onCompleted: () => {
      show.info('Faktúra bola označená ako uhradená.');
      setShowMarkPaidDialog(false);
      refetch?.();
    },
  });

  const [markUnpaid, { loading: markUnpaidLoading }] = useMutation<
    MarkInvoiceUnpaidMutation,
    MarkInvoiceUnpaidMutationVariables
  >(MarkInvoiceUnpaidDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa označiť faktúru ako neuhradenú.', e.message);
    },
    onCompleted: () => {
      show.info('Faktúra bola označená ako neuhradená.');
      refetch?.();
    },
  });

  const [copyInvoice, { loading: copyLoading }] = useMutation<CopyInvoiceMutation, CopyInvoiceMutationVariables>(
    CopyInvoiceDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa skopírovať faktúru.', e.message);
      },
      onCompleted: (data) => {
        if (data?.copyInvoice?.invoice) {
          show.info('Faktúra bola úspešne skopírovaná.');
          navigate(appPaths.invoice.navigate({ invoiceId: data.copyInvoice.invoice.id }));
        }
      },
    },
  );

  const [sendPaymentConfirmation, { loading: sendConfirmationLoading }] = useMutation<
    SendPaymentConfirmationMutation,
    SendPaymentConfirmationMutationVariables
  >(SendPaymentConfirmationDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa odoslať potvrdenie o úhrade.', e.message),
    onCompleted: () => {
      show.info('Potvrdenie o úhrade bolo úspešne odoslané.');
      refetch?.();
    },
  });

  const [sendInvoiceInformationEmail, { loading: sendInvoiceInfoLoading }] = useMutation<
    SendInvoiceInformationEmailMutation,
    SendInvoiceInformationEmailMutationVariables
  >(SendInvoiceInformationEmailDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa odoslať informačný email o faktúre.', e.message),
    onCompleted: () => {
      show.info('Informačný email o faktúre bol úspešne odoslaný.');
      setShowSendInvoiceInfoDialog(false);
      setInvoiceInfoEmails('');
      setIncludeDefaultRecipients(true);
      setInvoiceInfoEmailsError('');
      refetch?.();
    },
  });

  const [addComment] = useMutation<AddInvoiceCommentMutation, AddInvoiceCommentMutationVariables>(
    AddInvoiceCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa pridať komentár.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [updateComment] = useMutation<UpdateInvoiceCommentMutation, UpdateInvoiceCommentMutationVariables>(
    UpdateInvoiceCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa aktualizovať komentár.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [removeComment] = useMutation<RemoveInvoiceCommentMutation, RemoveInvoiceCommentMutationVariables>(
    RemoveInvoiceCommentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať komentár.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [addRow] = useMutation<AddInvoiceRowMutation, AddInvoiceRowMutationVariables>(AddInvoiceRowDocument, {
    onError: (e: Error) => {
      show.error('Nepodarilo sa pridať riadok.', e.message);
    },
    onCompleted: () => {
      show.info('Riadok bol pridaný.');
      refetch?.();
    },
  });

  const [updateRow] = useMutation<UpdateInvoiceRowMutation, UpdateInvoiceRowMutationVariables>(
    UpdateInvoiceRowDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa upraviť riadok.', e.message);
      },
      onCompleted: () => {
        show.info('Riadok bol upravený.');
        refetch?.();
      },
    },
  );

  const [removeRow] = useMutation<RemoveInvoiceRowMutation, RemoveInvoiceRowMutationVariables>(
    RemoveInvoiceRowDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa zmazať riadok.', e.message);
      },
      onCompleted: () => {
        show.info('Riadok bol zmazaný.');
        refetch?.();
      },
    },
  );

  const [moveRowUp] = useMutation<MoveInvoiceRowUpMutation, MoveInvoiceRowUpMutationVariables>(
    MoveInvoiceRowUpDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa posunúť riadok.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [moveRowDown] = useMutation<MoveInvoiceRowDownMutation, MoveInvoiceRowDownMutationVariables>(
    MoveInvoiceRowDownDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa posunúť riadok.', e.message);
      },
      onCompleted: () => {
        refetch?.();
      },
    },
  );

  const [deleteInvoicePayment] = useMutation<DeleteInvoicePaymentMutation, DeleteInvoicePaymentMutationVariables>(
    DeleteInvoicePaymentDocument,
    {
      onError: (e: Error) => {
        show.error('Nepodarilo sa odstrániť úhradu.', e.message);
      },
      onCompleted: () => {
        show.info('Úhrada bola odstránená.');
        refetch?.();
      },
    },
  );

  const [setUnitAssignments, { loading: assignmentsLoading }] = useMutation<
    SetInvoiceUnitAssignmentsMutation,
    SetInvoiceUnitAssignmentsMutationVariables
  >(SetInvoiceUnitAssignmentsDocument, {
    onError: (e: Error) => show.error('Nepodarilo sa uložiť priradenia k nehnuteľnostiam.', e.message),
    onCompleted: () => {
      show.info('Priradenia k nehnuteľnostiam boli úspešne uložené.');
      refetch?.();
    },
  });

  const handleFieldUpdate = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target as HTMLInputElement;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!invoice) return;

    await updateInvoice({
      variables: {
        invoiceId: invoice.id,
        input: {
          refNo: formData.refNo,
          description: formData.description,
          dueDate: formData.dueDate ? new Date(formData.dueDate).toISOString() : undefined,
        },
      },
    });
  };

  const handleDelete = async () => {
    if (!invoice) return;

    if (invoice.deletedAt) {
      await undeleteInvoice({
        variables: {
          invoiceId: invoice.id,
        },
      });
    } else {
      await deleteInvoice({
        variables: {
          invoiceId: invoice.id,
        },
      });
    }
  };

  const handleCopy = async () => {
    if (!invoice) return;

    await copyInvoice({
      variables: {
        invoiceId: invoice.id,
      },
    });
  };

  const handleRemovePayment = async (paymentId: string) => {
    setPaymentToDelete(paymentId);
  };

  const confirmRemovePayment = async () => {
    if (!paymentToDelete) return;

    await deleteInvoicePayment({
      variables: {
        id: paymentToDelete,
      },
    });
    setPaymentToDelete(null);
  };

  const handleSaveAssignments = async (assignments: UnitAssignmentRow[]) => {
    if (!invoice) return;

    await setUnitAssignments({
      variables: {
        invoiceId: invoice.id,
        assignments: assignments.map((r) => ({ unitId: r.unitId, amount: parseFloat(r.amount) })),
      },
    });
  };

  const parseInvoiceInfoEmails = (value: string): string[] =>
    value
      .split(/[\s,]+/)
      .map((email) => email.trim())
      .filter((email) => email.length > 0);

  const validateInvoiceInfoEmails = (value: string): string[] | null => {
    const emails = parseInvoiceInfoEmails(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmail = emails.find((email) => !emailRegex.test(email));
    if (invalidEmail) {
      setInvoiceInfoEmailsError(`Neplatná emailová adresa: ${invalidEmail}`);
      return null;
    }
    setInvoiceInfoEmailsError('');
    return emails;
  };

  const handleOpenInvoiceInfoDialog = () => {
    setShowSendInvoiceInfoDialog(true);
    setIncludeDefaultRecipients(true);
    setInvoiceInfoEmails('');
    setInvoiceInfoEmailsError('');
  };

  const handleSendInvoiceInformationEmail = () => {
    if (!invoice) return;

    const parsedEmails = validateInvoiceInfoEmails(invoiceInfoEmails);
    if (parsedEmails === null) {
      return;
    }

    if (!includeDefaultRecipients && parsedEmails.length === 0) {
      setInvoiceInfoEmailsError(
        'Zadajte aspoň jednu emailovú adresu alebo ponechajte zaškrtnutých štandardných príjemcov.',
      );
      return;
    }

    sendInvoiceInformationEmail({
      variables: {
        invoiceId: invoice.id,
        emails: invoiceInfoEmails.trim() || null,
        includeDefaultRecipients,
      },
    });
  };

  if (!invoice) {
    return (
      <BasePage
        title="Faktúra"
        loading={initialLoading}
        errorMsg={error ? 'Nepodarilo sa načítať detaily faktúry.' : undefined}
      >
        <div />
      </BasePage>
    );
  }

  const isPaid = !!invoice.paidAt;
  const isDeleted = !!invoice.deletedAt;
  const isDraft = invoice.isDraft;
  const canEdit = !isPaid && !isDeleted;
  const loading =
    updateLoading ||
    issueLoading ||
    deleteLoading ||
    undeleteLoading ||
    markPaidLoading ||
    markUnpaidLoading ||
    copyLoading ||
    sendConfirmationLoading ||
    sendInvoiceInfoLoading ||
    assignmentsLoading;
  const isLocked = !!invoice.deletedAt || loading;

  const pageTitle = isDraft ? 'Návrh faktúry' : `Faktúra #${invoice.invoiceNo}`;

  return (
    <BasePage title={pageTitle}>
      {isDraft && !isDeleted && (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
          <Typography variant="body1" color="text.primary" fontWeight="medium">
            Toto je návrh faktúry. Faktúra nebola ešte vystavená – nemá pridelené číslo ani dátum vystavenia.
          </Typography>
        </Box>
      )}
      {isDeleted && (
        <DeletedItemBanner
          text={`Táto faktúra bola zmazaná ${app.formatDate(invoice.deletedAt)}.`}
          undeleteLabel="Obnoviť"
          onUndelete={handleDelete}
        />
      )}
      {/* Contract link */}
      {invoice.contract && (
        <Box>
          <Typography variant="body2" color="text.secondary">
            <strong>Prenájom:</strong>{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() => navigate(appPaths.contract.navigate({ contractId: invoice.contract!.id }))}
              sx={{ cursor: 'pointer' }}
            >
              {invoice.contract.title}
            </Link>
          </Typography>
        </Box>
      )}
      <Grid container spacing={3}>
        {/* Left side - Invoice details and rows */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Stack spacing={3}>
            {/* Invoice header fields */}
            <Box component="form" noValidate>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="invoiceNo"
                    label="Číslo faktúry"
                    value={formData.invoiceNo}
                    onChange={handleFieldUpdate}
                    disabled
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="refNo"
                    label="Referencia/VS"
                    value={formData.refNo}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <FormInput
                    name="description"
                    label="Popis"
                    value={formData.description}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    variant="outlined"
                    type={{ type: 'multiLine', minRows: 2 }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="issuedAt"
                    label="Dátum vystavenia"
                    type="date"
                    value={formData.issuedAt}
                    onChange={handleFieldUpdate}
                    disabled
                    required
                    variant="outlined"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <FormInput
                    name="dueDate"
                    label="Dátum splatnosti"
                    type="date"
                    value={formData.dueDate}
                    onChange={handleFieldUpdate}
                    disabled={!canEdit || loading}
                    required
                    variant="outlined"
                  />
                </Grid>
              </Grid>

              {canEdit && (
                <Button variant="contained" onClick={handleSave} disabled={loading} sx={{ mt: 2 }}>
                  Uložiť zmeny
                </Button>
              )}
            </Box>

            <Divider />

            {/* Invoice rows */}
            {invoice && (
              <InvoiceRowEditor
                invoice={invoice}
                disabled={!canEdit}
                onAddRow={(row) => addRow({ variables: { invoiceId: invoice.id, input: row } })}
                onUpdateRow={(rowNumber, row) =>
                  updateRow({ variables: { invoiceId: invoice.id, rowNumber, input: row } })
                }
                onRemoveRow={(rowNumber) => removeRow({ variables: { invoiceId: invoice.id, rowNumber } })}
                onMoveRowUp={(rowNumber) => moveRowUp({ variables: { invoiceId: invoice.id, rowNumber } })}
                onMoveRowDown={(rowNumber) => moveRowDown({ variables: { invoiceId: invoice.id, rowNumber } })}
              />
            )}
          </Stack>
        </Grid>

        {/* Right side - Actions and comments */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Stack spacing={2}>
            {/* Action buttons */}
            <Box>
              <Stack spacing={1}>
                {isDraft && !isDeleted && (
                  <Button
                    onClick={() => setShowIssueDialog(true)}
                    disabled={isLocked}
                    color="success"
                    variant="contained"
                    fullWidth
                  >
                    Vystaviť faktúru
                  </Button>
                )}
                {!isDraft && (
                  <Button onClick={handleCopy} disabled={isLocked} color="primary" variant="outlined" fullWidth>
                    Kopírovať faktúru
                  </Button>
                )}
                {!isDeleted && !isDraft && (
                  <Button
                    onClick={handleOpenInvoiceInfoDialog}
                    disabled={isLocked}
                    color="info"
                    variant="outlined"
                    fullWidth
                  >
                    Poslať faktúru
                  </Button>
                )}
                <Button
                  onClick={handleDelete}
                  disabled={isLocked}
                  color={isDeleted ? 'primary' : 'error'}
                  variant="outlined"
                  fullWidth
                >
                  {isDeleted ? 'Obnoviť' : 'Zmazať'}
                </Button>
                {!isDeleted && !isDraft && (
                  <>
                    {isPaid ? (
                      <>
                        <Button
                          onClick={() =>
                            markUnpaid({
                              variables: {
                                invoiceId: invoice.id,
                              },
                            })
                          }
                          disabled={isLocked}
                          color="warning"
                          variant="outlined"
                          fullWidth
                        >
                          Označiť ako neuhradenú
                        </Button>
                        <Button
                          onClick={() => {
                            if (!invoice?.id) return;
                            sendPaymentConfirmation({ variables: { invoiceId: invoice.id } });
                          }}
                          disabled={isLocked}
                          color="info"
                          variant="outlined"
                          fullWidth
                        >
                          Odoslať potvrdenie o úhrade
                        </Button>
                      </>
                    ) : (
                      <Button
                        onClick={() => setShowMarkPaidDialog(true)}
                        disabled={isLocked}
                        color="success"
                        variant="outlined"
                        fullWidth
                      >
                        Označiť ako uhradenú
                      </Button>
                    )}
                  </>
                )}
              </Stack>
            </Box>

            <Divider />

            {/* Assignments section */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="h6">Úhrady</Typography>

                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => setShowPaymentDialog(true)}
                  disabled={isLocked || isDraft}
                >
                  Pridať
                </Button>
              </Stack>
              <Box>
                {isPaid && (
                  <Typography variant="body1" color="success.main">
                    Táto faktúra bola uhradená dňa {app.formatDate(invoice.paidAt)}
                  </Typography>
                )}
              </Box>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Uhradené: {app.formatNumber(invoice.paidAmount, 2)} € z {app.formatNumber(invoice.totalAmount, 2)} €
              </Typography>
              <Box sx={{ mt: 2 }}>
                <InvoicePaymentList
                  payments={invoice.payments || []}
                  context="invoice"
                  onRemove={handleRemovePayment}
                />
              </Box>
            </Box>

            <Divider />

            <InvoiceIncomeAssignmentPanel
              invoice={invoice}
              isLocked={!canEdit}
              assignmentsLoading={assignmentsLoading}
              onSaveAssignments={handleSaveAssignments}
            />

            <Divider />

            {/* Comments section */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Komentáre
              </Typography>
              <InputComment
                label="Pridať komentár"
                disabled={isLocked}
                onSubmit={(comment) => addComment({ variables: { invoiceId: invoice.id, input: { comment } } })}
                multiline
                clearOnSubmit
              />
              <Box maxHeight="20rem" overflow="auto" sx={{ mt: 2 }}>
                <CommentList
                  disabled={isLocked}
                  comments={invoice.comments}
                  onDelete={(c) => removeComment({ variables: { invoiceId: invoice.id, commentId: c.id } })}
                  onEdit={(commentId, comment) =>
                    updateComment({ variables: { invoiceId: invoice.id, input: { id: commentId, comment } } })
                  }
                />
              </Box>
            </Box>
          </Stack>
        </Grid>
      </Grid>

      {/* Mark as Paid Dialog */}
      {showMarkPaidDialog && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
          onClick={() => setShowMarkPaidDialog(false)}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              p: 3,
              minWidth: 300,
              maxWidth: 500,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Typography variant="h6" gutterBottom>
              Označiť ako uhradenú
            </Typography>
            <Box sx={{ mt: 2, mb: 3 }}>
              <FormInput
                name="paidDate"
                label="Dátum úhrady"
                type="date"
                value={paidDate}
                onChange={(e) => setPaidDate(e.target.value)}
                required
                variant="outlined"
              />
            </Box>
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button onClick={() => setShowMarkPaidDialog(false)} disabled={markPaidLoading}>
                Zrušiť
              </Button>
              <Button
                onClick={() =>
                  markPaid({
                    variables: {
                      invoiceId: invoice.id,
                      paidAt: new Date(paidDate).toISOString(),
                    },
                  })
                }
                variant="contained"
                disabled={markPaidLoading}
              >
                Potvrdiť
              </Button>
            </Stack>
          </Box>
        </Box>
      )}

      {/* Confirmation dialog for removing assignment */}
      {paymentToDelete && (
        <ConfirmationDialog
          open={!!paymentToDelete}
          title="Odstránenie úhrady"
          message="Naozaj chcete odstrániť túto úhradu?"
          onCancel={() => setPaymentToDelete(null)}
          onConfirm={confirmRemovePayment}
          cancelLabel="Zrušiť"
          confirmLabel="Odstrániť"
        />
      )}

      {/* Issue invoice confirmation dialog */}
      <ConfirmationDialog
        open={showIssueDialog}
        title="Vystavenie faktúry"
        message="Naozaj chcete vystaviť túto faktúru? Po vystavení bude pridelené číslo faktúry a dátum vystavenia."
        onCancel={() => setShowIssueDialog(false)}
        onConfirm={() => issueInvoice({ variables: { invoiceId: invoice.id } })}
        cancelLabel="Zrušiť"
        confirmLabel="Vystaviť faktúru"
      />

      {/* Payment dialog */}
      {showPaymentDialog && invoice && (
        <MoneyTransactionDialog
          open={showPaymentDialog}
          transaction={null}
          onClose={() => setShowPaymentDialog(false)}
          onRefresh={() => refetch?.()}
          invoiceAssignment={{
            invoiceId: invoice.id,
            invoiceAmount: invoice.totalAmount,
            paidAmount: invoice.paidAmount,
          }}
        />
      )}

      <Dialog
        open={showSendInvoiceInfoDialog}
        onClose={() => setShowSendInvoiceInfoDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Poslať faktúru e-mailom</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Ďalšie emailové adresy"
              value={invoiceInfoEmails}
              onChange={(e) => {
                setInvoiceInfoEmails(e.target.value);
                validateInvoiceInfoEmails(e.target.value);
              }}
              fullWidth
              multiline
              minRows={2}
              error={!!invoiceInfoEmailsError}
              helperText={invoiceInfoEmailsError || 'Adresy oddeľte medzerou a/alebo čiarkou'}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={includeDefaultRecipients}
                  onChange={(e) => setIncludeDefaultRecipients(e.target.checked)}
                />
              }
              label="Pridať aj štandardných príjemcov faktúr"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowSendInvoiceInfoDialog(false)} disabled={sendInvoiceInfoLoading}>
            Zrušiť
          </Button>
          <Button onClick={handleSendInvoiceInformationEmail} variant="contained" disabled={sendInvoiceInfoLoading}>
            Odoslať
          </Button>
        </DialogActions>
      </Dialog>
    </BasePage>
  );
}
